import { Router } from "express";

import { settingsController } from "../controllers/settings-controller";
import {
  authenticate,
  createRateLimit,
  requireEmailVerification,
} from "../middleware/auth-middleware";

const router = Router();

// Environment-aware rate limiting
const generalRateLimit = createRateLimit.general(); // 50 req/5min (prod) | unlimited (dev)

/**
 * @route   GET /api/v1/settings
 * @desc    Get user settings and preferences
 * @access  Private (requires authentication)
 */
router.get("/", authenticate, (req, res) =>
  settingsController.getSettings(req, res),
);

/**
 * @route   PUT /api/v1/settings/notifications
 * @desc    Update notification settings
 * @access  Private (requires authentication)
 * @body    { email?: boolean, push?: boolean, sms?: boolean }
 */
router.put(
  "/notifications",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req, res) => settingsController.updateNotificationSettings(req, res),
);

/**
 * @route   PUT /api/v1/settings/notifications/toggle
 * @desc    Toggle notification status (simple on/off)
 * @access  Private (requires authentication)
 * @body    { enabled: boolean }
 */
router.put(
  "/notifications/toggle",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req, res) => settingsController.toggleNotifications(req, res),
);

/**
 * @route   PUT /api/v1/settings/preferences
 * @desc    Update user preferences
 * @access  Private (requires authentication)
 * @body    { language?: string, timezone?: string, theme?: string }
 */
router.put(
  "/preferences",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req, res) => settingsController.updatePreferences(req, res),
);

/**
 * @route   PUT /api/v1/settings/privacy
 * @desc    Update privacy settings (Student only)
 * @access  Private (Student)
 * @body    { shareContactInfo?: boolean, shareAcademicInfo?: boolean, allowDirectMessages?: boolean }
 */
router.put(
  "/privacy",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req, res) => settingsController.updatePrivacySettings(req, res),
);

/**
 * @route   PUT /api/v1/settings/school
 * @desc    Update school settings (School Admin only)
 * @access  Private (School Admin)
 * @body    { allowStudentRegistration?: boolean, allowTeacherRegistration?: boolean, requireDocumentVerification?: boolean, autoApproveStudents?: boolean, autoApproveTeachers?: boolean }
 */
router.put(
  "/school",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req, res) => settingsController.updateSchoolSettings(req, res),
);

/**
 * @route   PUT /api/v1/settings/change-password
 * @desc    Change password for authenticated user
 * @access  Private (all roles)
 * @body    { currentPassword, newPassword }
 */
router.put(
  "/change-password",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req, res) => settingsController.changePassword(req, res),
);

/**
 * @route   GET /api/v1/settings/privacy-policy
 * @desc    Get privacy policy
 * @access  Private (all roles)
 */
router.get("/privacy-policy", authenticate, generalRateLimit, (req, res) => {
  settingsController.getPrivacyPolicy(req, res);
});

/**
 * @route   GET /api/v1/settings/about-us
 * @desc    Get about us information
 * @access  Private (all roles)
 */
router.get("/about-us", authenticate, generalRateLimit, (req, res) => {
  settingsController.getAboutUs(req, res);
});

/**
 * @route   GET /api/v1/settings/terms-conditions
 * @desc    Get terms & conditions
 * @access  Private (all roles)
 */
router.get("/terms-conditions", authenticate, generalRateLimit, (req, res) => {
  settingsController.getTermsConditions(req, res);
});

/**
 * @route   POST /api/v1/settings/help-feedback
 * @desc    Submit help request or feedback
 * @access  Private (all roles)
 * @body    { type, category, subject, description, priority?, attachments? }
 */
router.post(
  "/help-feedback",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req, res) => settingsController.submitHelpFeedback(req, res),
);

/**
 * @route   POST /api/v1/settings/help-request-school
 * @desc    Submit help request from school (Student only)
 * @access  Private (Student)
 * @body    { subject, description, priority?, attachments? }
 */
router.post(
  "/help-request-school",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req, res) => settingsController.submitHelpRequestFromSchool(req, res),
);

/**
 * @route   DELETE /api/v1/settings/account
 * @desc    Delete user account
 * @access  Private (all roles)
 * @body    { password, reason? }
 */
router.delete(
  "/account",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req, res) => settingsController.deleteAccount(req, res),
);

export default router;
